import React, { useState, useContext, useEffect } from 'react';
import { toast } from 'react-toastify';
import { AuthContext } from '../context/AuthContext';
import { FiEdit2, FiSave, FiX, FiCheck } from 'react-icons/fi';
import '../styles/UserDetails.css';

// Import specific functions from aws-amplify/auth
import { 
  fetchAuthSession, 
  updateUserAttributes,
  getCurrentUser,
  confirmUserAttribute,
  sendUserAttributeVerificationCode
} from 'aws-amplify/auth';

// Debug control - use i.e. DEBUG && console.log("whatever") and set below const true
const DEBUG = process.env.REACT_APP_DEBUG == 'enabled';

const UserDetails = ({ user }) => {
  const { setUser } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneUpdated, setPhoneUpdated] = useState(false);
  
  // Extract user attributes
  const [userAttributes, setUserAttributes] = useState(user.attributes || {});
  
  // Update local userAttributes state when user prop changes
  useEffect(() => {
    DEBUG && console.log('User object updated:', user);
    setUserAttributes(user.attributes || {});
  }, [user]);
  
  // Log attributes to help debug
  DEBUG && console.log('User attributes in UserDetails:', userAttributes);
  
  const accountName = userAttributes['custom:accountName'] || 
                      userAttributes.accountName || 
                      userAttributes['custom:account_name'] || 
                      userAttributes.account_name || 
                      'Not available';
                      
  const globalAccountID = userAttributes['custom:globalAccountID'] || 
                          userAttributes.globalAccountID || 
                          userAttributes['custom:global_account_id'] || 
                          userAttributes.global_account_id || 
                          'Not available';
  
  const [formData, setFormData] = useState({
    given_name: userAttributes.given_name || '',
    family_name: userAttributes.family_name || '',
    phone_number: userAttributes.phone_number || '',
    timeZone: userAttributes.timeZone || 'Default (Europe/Berlin)'
  });

  // Force a refresh of user session after component mounts or verification changes
  useEffect(() => {
    const fetchData = async () => {
      if (!verifying && !isEditing) {
        DEBUG && console.log('Refreshing user data');
        await refreshUserData();
      }
    };
  
    fetchData();
  }, [verifying, isEditing]);

  // Check if phone is verified - handle different possible formats
  const isPhoneVerified = 
    userAttributes.phone_number_verified === 'true' || 
    userAttributes.phone_number_verified === true || 
    userAttributes.phone_number_verified === 1;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleVerificationCodeChange = (e) => {
    setVerificationCode(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const phoneChanged = formData.phone_number !== userAttributes.phone_number;
      
      // Update user attributes with v6 style
      await updateUserAttributes({
        userAttributes: {
          given_name: formData.given_name,
          family_name: formData.family_name,
          name: formData.given_name+" "+formData.family_name,
          phone_number: formData.phone_number,
          'custom:timeZone': formData.timeZone
        }
      });
      
      // If phone number was changed, verify it
      if (phoneChanged && formData.phone_number) {
        setPhoneUpdated(true);
        // Send verification code immediately after updating phone
        await sendUserAttributeVerificationCode({
          userAttributeKey: 'phone_number'
        });
        toast.info('Verification code sent to your phone number');
      } else {
        // If no phone change, just update the user normally
        await refreshUserData();
        setIsEditing(false);
        toast.success('Profile updated successfully. You may need to log into Cloud Services Portal again to see changes reflected there');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const refreshUserData = async () => {
    try {
      const currentUser = await getCurrentUser();
      
      // Fetch fresh session with latest token
      const session = await fetchAuthSession({ forceRefresh: true });
      
      // Extract user attributes from token payload
      const tokenPayload = session.tokens?.idToken?.payload || {};
      
      // Get all attributes that might be in the token
      const updatedUserAttributes = {
        ...tokenPayload,
        // Ensure verification status is included
        phone_number_verified: tokenPayload.phone_number_verified,
        email_verified: tokenPayload.email_verified
      };
      
      DEBUG && console.log('Raw session data:', session);
      DEBUG && console.log('Token payload:', tokenPayload);
      DEBUG && console.log('Refreshed user attributes:', updatedUserAttributes);
      
      // Create new user object with updated attributes
      const updatedUser = {
        ...user,
        attributes: updatedUserAttributes
      };
      
      // Update user in context
      setUser(updatedUser);
      
      return updatedUserAttributes;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      throw error;
    }
  };
  
  const handleVerifyPhone = async () => {
    setVerifying(true);
    try {
      await confirmUserAttribute({
        userAttributeKey: 'phone_number',
        confirmationCode: verificationCode
      });
      
      // After verification, refresh the user data to get the updated verification status
      const updatedAttributes = await refreshUserData();
      
      setVerifying(false);
      setPhoneUpdated(false);
      setIsEditing(false);
      setVerificationCode('');
      toast.success('Phone number verified successfully');
      
      // Log the updated attributes to check verification status
      DEBUG && console.log('Updated attributes after verification:', updatedAttributes);
    } catch (error) {
      console.error('Error verifying phone number:', error);
      toast.error(error.message || 'Failed to verify phone number');
      setVerifying(false);
    }
  };
  
  const resendVerificationCode = async () => {
    try {

      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number'
      });
      toast.info('Verification code sent to your phone number');
    } catch (error) {
      console.error('Error sending verification code:', error);
      toast.error(error.message || 'Failed to send verification code');
    }
  };

  const verifyNow = async () => {
    setPhoneUpdated(true);
  };
  
  const cancelEdit = () => {
    // Reset form data to original values
    setFormData({
      given_name: userAttributes.given_name || '',
      family_name: userAttributes.family_name || '',
      phone_number: userAttributes.phone_number || '',
      timeZone: userAttributes.timeZone || 'Default (Europe/Berlin)'
    });
    setIsEditing(false);
    setPhoneUpdated(false);
    setVerificationCode('');
  };

  return (
    <div className="user-details">
      <div className="details-header">
        <h2>Profile Information</h2>
        {!isEditing && !phoneUpdated ? (
          <button 
            className="edit-button"
            onClick={() => setIsEditing(true)}
            aria-label="Edit profile"
          >
            <FiEdit2 /> Edit
          </button>
        ) : (
          <div className="edit-controls">
            <button 
              className="cancel-button"
              onClick={cancelEdit}
              disabled={loading || verifying}
              aria-label="Cancel editing"
            >
              <FiX /> Cancel
            </button>
            {!phoneUpdated && (
              <button 
                className="save-button"
                onClick={handleSubmit}
                disabled={loading || verifying}
                aria-label="Save changes"
              >
                <FiSave /> {loading ? 'Saving...' : 'Save'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="user-info">
        <div className="info-section">
          <h3>Account Information</h3>
          <div className="info-field">
            <label>Email Address</label>
            <span>{userAttributes.email || ''}</span>
            <small>(Email address cannot be changed)</small>
          </div>
          
          <div className="info-field">
            <label>Account Name</label>
            <span>{userAttributes.companyName}</span>
          </div>
          
          <div className="info-field">
            <label>Global Account ID</label>
            <span>{userAttributes.globalId}</span>
          </div>

          <div className="info-field">
            <label>Assigned Roles</label>
            <span>{userAttributes.cloudServicesRoles ? userAttributes.cloudServicesRoles : 'None'}</span>
          </div>    

          <div className="info-field">
            <label>App Portal Access</label>
            <span>{userAttributes.appPortalAccess ? 'Yes' : 'No'}</span>
          </div>

          <div className="info-field">
            <label>App Creator Portal Access</label>
            <span>{userAttributes.appCreatorAccess ? 'Yes' : 'No'}</span>
          </div>
       
        </div>

        <div className="info-section">
          <h3>Personal Information</h3>
          {phoneUpdated ? (
            <div className="verification-section">
              <h4>Verify Your Phone Number</h4>
              <p>A verification code has been sent to your phone number. Please enter it below to verify your number.</p>
              
              <div className="form-field">
                <label htmlFor="verification_code">Verification Code</label>
                <input
                  id="verification_code"
                  name="verification_code"
                  type="text"
                  value={verificationCode}
                  onChange={handleVerificationCodeChange}
                  placeholder="Enter verification code"
                  required
                />
              </div>
              
              <div className="verification-actions">
                <button 
                  className="resend-button"
                  onClick={resendVerificationCode}
                  disabled={verifying}
                >
                  Resend Code
                </button>
                <button 
                  className="verify-button"
                  onClick={handleVerifyPhone}
                  disabled={!verificationCode || verifying}
                >
                  <FiCheck /> {verifying ? 'Verifying...' : 'Verify'}
                </button>
              </div>
            </div>
          ) : isEditing ? (
            <form className="edit-form">
              <div className="form-field">
                <label htmlFor="given_name">First Name</label>
                <input
                  id="given_name"
                  name="given_name"
                  type="text"
                  value={formData.given_name}
                  onChange={handleChange}
                  required
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="family_name">Last Name</label>
                <input
                  id="family_name"
                  name="family_name"
                  type="text"
                  value={formData.family_name}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
              <label htmlFor="timeZone">Time Zone</label>  {/* Added: Time Zone label */}
              <select
                id="timeZone"
                name="timeZone"
                value={formData.timeZone}
                onChange={handleChange}
              >{/* Added: Time Zone dropdown */}
                <option value="">Default (Europe/Berlin)</option> {/* Default option if no custom time zone */}
                <option value="Australia/NSW">Australia/NSW</option>
                <option value="Canada/Central">Canada/Central</option>
                <option value="Canada/Eastern">Canada/Eastern</option>
                <option value="Canada/Mountain">Canada/Mountain</option>
                <option value="Canada/Pacific">Canada/Pacific</option>
                <option value="Europe/Amsterdam">Europe/Amsterdam</option>
                <option value="Europe/Berlin">Europe/Berlin</option>
                <option value="Europe/Brussels">Europe/Brussels</option>
                <option value="Europe/Copenhagen">Europe/Copenhagen</option>
                <option value="Europe/Dublin">Europe/Dublin</option>
                <option value="Europe/London">Europe/London</option>
                <option value="Europe/Madrid">Europe/Madrid</option>
                <option value="Europe/Paris">Europe/Paris</option>
                <option value="Europe/Rome">Europe/Rome</option>
                <option value="Europe/Stockholm">Europe/Stockholm</option>
                <option value="Europe/Zurich">Europe/Zurich</option>
                <option value="GMT">GMT</option>
                <option value="Hongkong">Hongkong</option>
                <option value="US/Arizona">US/Arizona</option>
                <option value="US/Central">US/Central</option>
                <option value="US/Eastern">US/Eastern</option>
                <option value="US/Hawaii">US/Hawaii</option>
                <option value="US/Mountain">US/Mountain</option>
                <option value="US/Pacific">US/Pacific</option>
              </select>
            </div>
              
              <div className="form-field">
                <label htmlFor="phone_number">Phone Number</label>
                <input
                  id="phone_number"
                  name="phone_number"
                  type="tel"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="+1234567890"
                  required
                />
                <small>Please include country code (e.g., +1 for US)</small>
              </div>
            </form>
          ) : (
            <>
              <div className="info-field">
                <label>First Name</label>
                <span>{userAttributes.given_name || 'Not set'}</span>
              </div>
              
              <div className="info-field">
                <label>Last Name</label>
                <span>{userAttributes.family_name || 'Not set'}</span>
              </div>

              <div className="info-field">
                <label>Time Zone</label>
                <span>{userAttributes.timeZone || 'Default (Europe/Berlin)'}</span>
              </div>
              
              <div className="info-field">
                <label>Phone Number</label>
                <span>
                  {userAttributes.phone_number || 'Not set'}
                  {userAttributes.phone_number && (
                    <span className={`verification-status ${isPhoneVerified ? 'verified' : 'unverified'}`}>
                      {isPhoneVerified ? ' (Verified)' : ' (Not Verified)'}
                    </span>
                  )}
                </span>
                {userAttributes.phone_number && !isPhoneVerified && (
                  <button 
                    className="verify-phone-button"
                    onClick={verifyNow}
                  >
                    Verify Now
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;